import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ProductCard from "./ProductCard";
import SkeletonGrid from "../common/SkeletonGrid";
import Reveal from "../common/Reveal";
import { API_BASE as API, cachedFetch } from "../../api/api";
import "./ProductRail.css";

const DOT_COUNT = 3;

const ProductRail = ({
  eyebrow,
  title,
  viewAllTo,
  endpoint,
  cols = 4,
  dark = false,
  lazy = false,
  hideIfEmpty = false,
  onAddToCart,
  // Emporia Brain v3.1, Phase 1 (P1.3) — the rail's slug, forwarded to each
  // ProductCard so it can fire viewable rail_impression/rail_click events.
  // Optional and null by default: only Home.jsx's backend-driven rails have
  // a real slug to stamp; other ProductRail usages (e.g. ProductDetailPage's
  // "Most Popular") simply don't get rail-level measurement yet.
  railKey = null,
}) => {
  const ref = useRef(null);
  const gridRef = useRef(null);
  const [inView, setInView] = useState(!lazy);
  const [items, setItems] = useState(null); // null = not yet loaded
  // Real co-purchase pairs (GET /rails/bundles/home only — every other
  // rail's response has this key present but null, per RailHomeRead).
  // null/empty here just means "render the flat grid below," the same
  // behavior this component always had before pairs existed.
  const [pairs, setPairs] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [activeDot, setActiveDot] = useState(0);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px 200px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [inView]);

  useEffect(() => {
    if (!inView) return;
    setItems(null);
    setPairs(null);
    setHidden(false);
    cachedFetch(`${API}${endpoint}`)
      .then((data) => {
        // Two response shapes hit this component: a plain array (the older
        // /products/... endpoints, e.g. ProductDetailPage's "Most Popular"
        // rail) and the rail engine's { hide, items, pairs } (GET
        // /rails/{slug}/home) — under min_to_show eligible products,
        // hide=true means render nothing, never pad with filler.
        if (Array.isArray(data)) {
          setItems(data);
        } else {
          setHidden(!!data?.hide);
          setItems(Array.isArray(data?.items) ? data.items : []);
          setPairs(Array.isArray(data?.pairs) && data.pairs.length > 0 ? data.pairs : null);
        }
      })
      .catch(() => setItems([]));
  }, [inView, endpoint]);

  // Tracks horizontal scroll position on the mobile swipe layout (see
  // ProductRail.css ≤860px) so the dots below the rail reflect where the
  // shopper actually is — not just decorative.
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const onScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const ratio = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
      setActiveDot(Math.min(DOT_COUNT - 1, Math.floor(ratio * DOT_COUNT)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [items]);

  const scrollToDot = (i) => {
    const el = gridRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollTo({ left: (maxScroll * i) / (DOT_COUNT - 1), behavior: "smooth" });
  };

  if (hidden) return null;
  if (hideIfEmpty && items !== null && items.length === 0) return null;

  const hasPairs = !!pairs && pairs.length > 0;
  // Six-across rails (the homepage) get their own class: it sets the column
  // count for the loading skeleton too, and carries the wider 3-column
  // breakpoint in ProductRail.css. 4-column rails keep the 1100px rule.
  const sixClass = cols === 6 ? " pr-grid--six" : "";
  // Only the bundles rail's own flat-fallback (real pairs unavailable,
  // showing the plain product list instead) gets the centered/non-stretched
  // treatment — every other rail's flat grid is unaffected.
  const isBundleRail = railKey === "bundles";
  const dotCount = hasPairs ? pairs.length : items ? items.length : 0;
  const dots = dotCount > 1 && (
    <div className="pr-dots" role="tablist" aria-label={`${title} — swipe for more`}>
      {Array.from({ length: DOT_COUNT }, (_, i) => (
        <button
          key={i}
          type="button"
          role="tab"
          className={`pr-dot${activeDot === i ? " pr-dot-active" : ""}`}
          aria-selected={activeDot === i}
          aria-label={`Go to ${i === 0 ? "start" : i === DOT_COUNT - 1 ? "end" : "middle"} of ${title}`}
          onClick={() => scrollToDot(i)}
        />
      ))}
    </div>
  );

  return (
    <section ref={ref} className={`section product-rail${dark ? " section-alt" : ""}`}>
      <div className="section-header">
        <div>
          <p className="section-eye">{eyebrow}</p>
          <h2 className="section-title">{title}</h2>
        </div>
        {viewAllTo && <Link to={viewAllTo} className="link-underline">View all →</Link>}
      </div>
      {items === null ? (
        <SkeletonGrid count={cols} className={`pr-grid${sixClass}`} />
      ) : hasPairs ? (
        <>
          <Reveal as="div" ref={gridRef} className="pr-pairs-grid reveal">
            {pairs.map((pair, i) => (
              <div key={`${pair.product_a.id}-${pair.product_b.id}`} className="pr-pair-card" style={{ "--i": i }}>
                <div className="grid-item-anim">
                  <ProductCard
                    product={pair.product_a}
                    onAddToCart={onAddToCart}
                    dark={dark}
                    railKey={railKey}
                    railPosition={i * 2}
                  />
                </div>
                <span className="pr-pair-connector" aria-hidden="true">
                  <span className="pr-pair-plus">+</span>
                </span>
                <div className="grid-item-anim">
                  <ProductCard
                    product={pair.product_b}
                    onAddToCart={onAddToCart}
                    dark={dark}
                    railKey={railKey}
                    railPosition={i * 2 + 1}
                  />
                </div>
              </div>
            ))}
          </Reveal>
          {dots}
        </>
      ) : (
        <>
          <Reveal
            as="div"
            ref={gridRef}
            className={`pr-grid${sixClass}${isBundleRail ? " pr-grid--centered" : ""} reveal`}
            style={{ "--cols": cols }}
          >
            {items.map((p, i) => (
              <div key={p.id} className="grid-item-anim" style={{ "--i": i }}>
                <ProductCard
                  product={p}
                  onAddToCart={onAddToCart}
                  dark={dark}
                  railKey={railKey}
                  railPosition={i}
                />
              </div>
            ))}
          </Reveal>
          {dots}
        </>
      )}
    </section>
  );
};

export default ProductRail;
